import { RequestOffersPanel, MyOffersPanel, PassengerPriceChooser } from "@/components/price/PriceOffers";
import { requestFare, fareTierLabel } from "@/lib/priceFare";
import ScheduleField, { toLocalInput } from "@/components/price/ScheduleField";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";


import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  MapPin, Users, Calendar, Menu, X, Zap,
  Star, Clock, Check, ArrowLeft, Ticket, Phone, Car, CreditCard,
  Route as RouteIcon, MessageCircle, ArrowRight, LifeBuoy, Search,
} from "lucide-react";
import PriceBottomTabs from "@/components/price/PriceBottomTabs";
import PriceHubPanel from "@/components/price/PriceHubPanel";
import PinnedTripBanner from "@/components/price/PinnedTripBanner";
import NearbyTripsCarousel from "@/components/price/NearbyTripsCarousel";
import TripThreadsSheet, { useTripThreads } from "@/components/price/TripThreadsSheet";

const fmtDay = (d) => {
  try { return new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }); } catch { return d; }
};
import AddressField from "@/components/AddressField";
import { api } from "@/lib/api";
import RateModal from "@/components/price/RateModal";
import RoutePoints from "@/components/price/RoutePoints";
import HistorySheet from "@/components/price/HistorySheet";
import ChatSheet from "@/components/price/ChatSheet";
import PublishedTripsScreen from "@/components/price/PublishedTripsScreen";
import PriceProfileSheet from "@/components/price/PriceProfileSheet";
import PriceSurveyPopup from "@/components/price/PriceSurveyPopup";
import AdminPopupCenter from "@/components/price/AdminPopupCenter";
import { PromoBanner, useGlobalPromo } from "@/components/price/PromoBanner";
import PriceSupportFAB from "@/components/price/PriceSupportFAB";
import { PriceScooterIcon } from "@/components/price/PriceIllustrations";
import { flushPendingPhoto } from "@/lib/pendingPhoto";
import { isNativeStripeAvailable } from "@/lib/stripeNative";
import ClientHomeChoices from "@/components/price/ClientHomeChoices";
import ScheduledRidesSheet, { fmtSched } from "@/components/price/ScheduledRidesSheet";
import ScheduledConfirmScreen from "@/components/price/ScheduledConfirmScreen";
import WearNegotiationPopup from "@/components/price/WearNegotiationPopup";
import DocumentsRequiredPopup from "@/components/price/DocumentsRequiredPopup";


/* --- Pricing (cartoon covoiturage : moins cher qu'Uber) ------------
   Défauts baked-in en fallback. Le vrai barème vient de l'API publique
   `/price/pricing-config` (modifiable par l'admin), fetché au mount.
--------------------------------------------------------------------- */
const PRICE_DEFAULTS = {
  base_eur: 5,
  per_km_eur: 0.7,
  min_fare_eur: 2,
  uber_base_eur: 2,
  uber_per_km_eur: 1.5,
};

function haversineKm(a, b) {
  if (!a || !b) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  return R * c;
}

/**
 * Price — Passenger (covoiturage) dashboard.
 *
 *  1) SEARCH FORM : from, to, date, seats + "Rechercher" → appelle
 *     /api/price/trips/search et bascule sur l'écran RESULTS.
 *  2) RESULTS : liste des trajets qui matchent, avec la note du covoitureur.
 *     Chaque carte a un CTA "Réserver X place(s)" → /api/price/trips/{id}/book.
 *  3) MY BOOKINGS : bouton "Mes réservations" en haut → sheet listant les
 *     réservations passées + bouton "Noter le covoitureur" pour chaque trajet
 *     terminé non encore noté.
 */
export default function ClientDashboard() {
  const { user, profile, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    from: "",
    to: "",
    date: new Date().toISOString().slice(0, 10),
    seats: 1,
    accept_two_wheels: false, // passager seul peut accepter moto/scooter
    has_helmet: null,         // null = pas encore choisi ; true/false quand 2 roues
  });
  const [fromCoord, setFromCoord] = useState(null); // {lng, lat}
  const [toCoord, setToCoord] = useState(null);
  const [proximity, setProximity] = useState(null); // "lng,lat"
  const [pricing, setPricing] = useState(PRICE_DEFAULTS);
  const [view, setView] = useState("search"); // search | published | live_waiting | driver_found
  const [reservationsOpen, setReservationsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [rateSeat, setRateSeat] = useState(null);
  const [mySeats, setMySeats] = useState([]);
  const [liveRequest, setLiveRequest] = useState(null); // ride request en cours
  const [rateLive, setRateLive] = useState(null); // ride request terminé à noter
  const [paymentMethod, setPaymentMethod] = useState("cash"); // "cash" | "card"
  const [mode, setMode] = useState(null); // null (accueil) | "instant" | "scheduled"
  const [scheduledAt, setScheduledAt] = useState(() => toLocalInput(new Date(Math.ceil((Date.now() + 2 * 3600e3) / 300e3) * 300e3)));
  const [scheduledRides, setScheduledRides] = useState([]);
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [confirmRide, setConfirmRide] = useState(null);
  const [tab, setTab] = useState("home"); // home | activity | messages
  const [threadsOpen, setThreadsOpen] = useState(false);
  const threads = useTripThreads("client");
  const followedRef = useRef(new Set());
  // Distance ROUTIÈRE RÉELLE Mapbox (km) pour `fromCoord → toCoord`.
  // Mise à jour en async dès que les 2 coords sont fixées. En attendant
  // la réponse, on affiche l'estimation haversine (vol d'oiseau) comme
  // placeholder — l'UI passe à la vraie distance en 1-2s max. Évite de
  // montrer un prix trop bas puis de le monter au checkout.
  const [routeKm, setRouteKm] = useState(null);
  const [proposedPrice, setProposedPrice] = useState(null); // prix libre passager (≥ seuil longue distance)
  useEffect(() => {
    if (!fromCoord || !toCoord) {
      setRouteKm(null);
      return;
    }
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) return; // garde l'estimation haversine
    const ac = new AbortController();
    (async () => {
      try {
        const url =
          `https://api.mapbox.com/directions/v5/mapbox/driving/` +
          `${fromCoord.lng},${fromCoord.lat};${toCoord.lng},${toCoord.lat}` +
          `?access_token=${token}&overview=false&alternatives=true`;
        const r = await fetch(url, { signal: ac.signal });
        if (!r.ok) return;
        const j = await r.json();
        const routes = Array.isArray(j?.routes) ? j.routes : [];
        if (!routes.length) return;
        const shortestMeters = Math.min(...routes.map((rt) => Number(rt.distance) || Infinity));
        if (Number.isFinite(shortestMeters)) {
          setRouteKm(shortestMeters / 1000);
        }
      } catch (_e) { /* fallback silencieux sur haversine */ }
    })();
    return () => ac.abort();
  }, [fromCoord, toCoord]);

  // Persist the currently-active live request id so a push notification
  // click doesn't lose the in-progress screen. On mount we look it up
  // and, if it's still pending/accepted, restore the driver_found view.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const stored = localStorage.getItem("price_active_ride_request_id");
        if (!stored) return;
        const r = await api.get(`/price/ride-requests/${stored}`);
        const req = r.data?.request;
        if (cancel || !req) return;
        if (["accepted", "passenger_boarding", "in_progress"].includes(req.status)) {
          setLiveRequest(req);
          setView("driver_found");
        } else if (req.status === "pending" || req.status === "pending_payment") {
          setLiveRequest(req);
          setView("live_waiting");
        } else {
          localStorage.removeItem("price_active_ride_request_id");
        }
      } catch (_e) {
        localStorage.removeItem("price_active_ride_request_id");
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Fetch dynamic pricing config once — admin can change base + per_km
  // rate live and every new dashboard mount picks up the new values.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/price/pricing-config");
        if (!cancel && r.data) setPricing({ ...PRICE_DEFAULTS, ...r.data });
      } catch (_e) { /* fallback to defaults */ }
    })();
    return () => { cancel = true; };
  }, []);

  const loadMySeats = useCallback(async () => {
    try {
      const r = await api.get("/price/seats/mine");
      setMySeats(r.data?.seats || []);
    } catch (_e) { /* silencieux */ }
  }, []);

  useEffect(() => { loadMySeats(); }, [loadMySeats]);

  const loadScheduled = useCallback(async () => {
    try {
      const r = await api.get("/price/me/scheduled-rides");
      setScheduledRides(r.data?.rides || []);
    } catch (_e) { /* silencieux */ }
  }, []);
  useEffect(() => {
    loadScheduled();
    const t = setInterval(() => { if (!document.hidden) loadScheduled(); }, 20000);
    return () => clearInterval(t);
  }, [loadScheduled]);

  const followRide = useCallback((ride) => {
    localStorage.setItem("price_active_ride_request_id", ride.id);
    setLiveRequest(ride);
    setScheduledOpen(false);
    setView("driver_found");
  }, []);
  // Course programmée devenue active (30 min avant) → on bascule sur le suivi.
  useEffect(() => {
    const live = scheduledRides.find((r) => ["accepted", "passenger_boarding", "in_progress"].includes(r.status));
    if (live && view === "search" && !followedRef.current.has(live.id)) {
      followedRef.current.add(live.id);
      toast.success("Ta course programmée commence — suis ton covoitureur 🚗");
      followRide(live);
    }
  }, [scheduledRides, view, followRide]);

  const firstName =
    profile?.full_name?.split(" ")[0] ||
    (user?.email || "").split("@")[0] ||
    "Voyageur";
  const pinnedSeat = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return mySeats
      .filter((s) => s.status === "confirmed" && (s.date || "") >= today)
      .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`))[0] || null;
  }, [mySeats]);

  // Hook promo globale — lit en direct l'état envoyé par l'admin via
  // GET /api/price/promo/active (poll 30s). Alimente l'affichage barré
  // du prix sur l'estimation + la bannière rouge en haut de la home.
  const activePromo = useGlobalPromo();
  const promoPct = activePromo?.active ? Number(activePromo.percent_off || 0) : 0;

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSearch = form.from.trim().length > 1 && form.to.trim().length > 1 && fromCoord && toCoord;

  // Live price estimate — computed as soon as both endpoints have coords.
  // Live price estimate — computed as soon as both endpoints have coords.
  // Si une promo globale est active, on calcule aussi `discounted` (prix
  // qui sera réellement facturé) et `discount` (économie en €).
  // Trajets ≥ 50 km : le partage des frais est réservé aux trajets publiés ;
  // les demandes restent au prix frais de base + km (+ supplément programmé).

  const priceInfo = useMemo(() => {
    if (!fromCoord || !toCoord) return null;
    // Préférer la distance ROUTIÈRE réelle (Mapbox Directions) dès
    // qu'elle est dispo — c'est elle qui sera utilisée côté backend
    // pour calculer le prix exact facturé. L'estimation haversine
    // sert de placeholder le temps que Mapbox réponde (1-2s).
    const km = routeKm != null ? routeKm : haversineKm(fromCoord, toCoord);
    if (!km || km < 0.1) return null;
    const seats = Math.max(1, Number(form.seats) || 1);
    const fare = requestFare(pricing, km, mode === "scheduled" ? scheduledAt : null);
    const total = fare.price;
    const discounted = promoPct > 0 ? Math.max(0, total * (1 - promoPct / 100)) : total;
    const discount = total - discounted;
    const perSeat = discounted / seats;
    const uber = pricing.uber_base_eur + km * pricing.uber_per_km_eur;
    return {
      km,
      distance_is_route: routeKm != null,
      total,            // prix brut (avant promo)
      discounted,       // prix facturé (après promo)
      discount,         // économie €
      perSeat,
      uber,
      savings: Math.max(0, uber - discounted),
      cheaper: Math.round((1 - discounted / Math.max(uber, 0.01)) * 100),
      long: null,
      instant: fare.instant,
      tierLabel: fareTierLabel(pricing, fare.tier),
    };
  }, [fromCoord, toCoord, form.seats, pricing, promoPct, routeKm, mode, scheduledAt]);
  const negotiable = !!priceInfo && priceInfo.km >= Number(pricing.negotiation_from_km ?? 80);
  useEffect(() => { setProposedPrice(null); }, [fromCoord, toCoord]);

  const pendingRatingSeats = useMemo(
    () => mySeats.filter((s) => s.status === "completed" && !s.rated_by_client),
    [mySeats]
  );

  useEffect(() => {
    if (view === "live_waiting" || view === "driver_found") setMode(null);
  }, [view]);

  const onSearch = (e) => {
    e.preventDefault();
    if (!canSearch) {
      toast.error("Choisis un départ et une arrivée sur la carte");
      return;
    }
    sendLiveRequest();
  };

  const cancelSeat = async (seatId) => {
    if (!window.confirm("Annuler ta réservation ?")) return;
    try {
      await api.post(`/price/seats/${seatId}/cancel`);
      toast("Réservation annulée");
      loadMySeats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const sendLiveRequest = useCallback(async () => {
    if (!fromCoord || !toCoord) {
      toast.error("Choisis un départ et une arrivée sur la carte");
      return;
    }
    // Feb 2026 — si le passager accepte les 2 roues il DOIT dire s'il
    // a un casque, sinon les motards recevraient la demande sans savoir.
    if ((Number(form.seats) || 1) === 1 && form.accept_two_wheels && form.has_helmet === null) {
      toast.error("Indique si tu as un casque avant d'envoyer la demande");
      return;
    }
    const price = priceInfo?.discounted
      ? Number(priceInfo.discounted.toFixed(2))
      : priceInfo?.total
      ? Number(priceInfo.total.toFixed(2))
      : 0;
    try {
      const body = {
        from_address: form.from.trim(),
        from_coord: fromCoord,
        to_address: form.to.trim(),
        to_coord: toCoord,
        seats: Number(form.seats) || 1,
        price_eur: price,
        notes: "",
        payment_method: paymentMethod,
        // Feb 2026 : le passager solo peut accepter les 2 roues et
        // déclarer s'il a son casque. Le backend filtre le broadcast et
        // affiche ces infos en gros côté motard.
        accept_two_wheels: (Number(form.seats) || 1) === 1 ? !!form.accept_two_wheels : false,
        has_helmet: (Number(form.seats) || 1) === 1 && form.accept_two_wheels ? form.has_helmet : null,
      };
      if (negotiable && proposedPrice) body.proposed_price_eur = proposedPrice;
      // On envoie toujours origin_url pour permettre au backend de
      // créer une Stripe Checkout Session. Sur mobile natif, on ouvre
      // ensuite le checkout_url dans un in-app browser (SFSafariView)
      // — Stripe Checkout gère Apple Pay natif côté serveur, aucun
      // merchant iOS à configurer, aucun crash du PaymentSheet.
      if (mode === "scheduled") {
        const when = new Date(scheduledAt);
        if (Number.isNaN(when.getTime()) || when.getTime() < Date.now() + 59 * 60e3) {
          toast.error("Programme ta course au moins 1 h à l'avance");
          return;
        }
        body.scheduled_at = when.toISOString();
        const r = await api.post("/price/ride-requests", body);
        try { navigator.vibrate?.([60, 40, 60]); } catch (_e) { /* ignore */ }
        toast.dismiss();
        setConfirmRide(r.data.request);
        setMode(null);
        loadScheduled();
        return;
      }
      if (paymentMethod === "card") {
        body.origin_url = window.location.origin;
      }
      const r = await api.post("/price/ride-requests", body);
      const req = r.data?.request;
      if (req?.id) localStorage.setItem("price_active_ride_request_id", req.id);

      // ─── Card branch — Stripe Checkout dans in-app browser ────────
      if (paymentMethod === "card" && r.data?.checkout_url) {
        toast("Ouverture du paiement…");
        if (isNativeStripeAvailable()) {
          const { Browser } = await import("@capacitor/browser");
          const listener = await Browser.addListener("browserFinished", async () => {
            listener.remove();
            // Le webhook Stripe passe la demande en "paid" ; on poll
            // le serveur pour voir si le paiement est bien arrivé.
            setTimeout(async () => {
              try {
                const check = await api.get(`/price/ride-requests/${req.id}`);
                const updated = check.data?.request;
                if (updated?.status === "pending" || updated?.payment_status === "paid") {
                  setLiveRequest(updated);
                  toast.success("Paiement reçu — recherche d'un covoitureur…");
                  setView("live_waiting");
                } else {
                  toast("Paiement annulé");
                  try { await api.post(`/price/ride-requests/${req.id}/cancel`); } catch (_e) { /* silent */ }
                  localStorage.removeItem("price_active_ride_request_id");
                }
              } catch (_e) { /* silent */ }
            }, 2000);
          });
          await Browser.open({ url: r.data.checkout_url, presentationStyle: "popover" });
          return;
        }
        // Web fallback — full page redirect.
        window.location.href = r.data.checkout_url;
        return;
      }

      // ─── Cash branch — direct broadcast, already done server-side ─
      setLiveRequest(req || null);
      toast.success(`Demande envoyée à ${r.data?.notified_drivers || 0} covoitureur(s)`, { position: "bottom-center", duration: 2500 });
      setView("live_waiting");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi");
    }
  }, [fromCoord, toCoord, form.from, form.to, form.seats, form.accept_two_wheels, form.has_helmet, priceInfo, paymentMethod, mode, scheduledAt, loadScheduled, negotiable, proposedPrice]);

  const cancelLiveRequest = useCallback(async () => {
    localStorage.removeItem("price_active_ride_request_id");
    if (!liveRequest?.id) {
      setView("search");
      return;
    }
    try {
      const r = await api.post(`/price/ride-requests/${liveRequest.id}/cancel`);
      const refunded = r.data?.refunded_eur || 0;
      toast(refunded > 0 ? `Demande annulée — ${refunded.toFixed(2)}€ remboursés sur ta carte` : "Demande annulée");
    } catch (_e) { /* silencieux */ }
    setLiveRequest(null);
    setView("search");
  }, [liveRequest]);

  // Poll the live request state (pending → accepted → passenger_boarding
  // → in_progress → completed / cancelled).
  useEffect(() => {
    if (!liveRequest?.id) return;
    if (view !== "live_waiting" && view !== "driver_found") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/price/ride-requests/${liveRequest.id}`);
        const req = r.data?.request;
        if (!req) return;
        setLiveRequest(req);
        if (["accepted", "passenger_boarding", "in_progress"].includes(req.status) && view === "live_waiting") {
          setView("driver_found");
        } else if (req.status === "pending" && view === "driver_found") {
          setView("live_waiting");
          toast("Ton covoitureur a annulé — on relance ta demande aux autres covoitureurs 🔁", { duration: 6000 });
        } else if (req.status === "completed") {
          localStorage.removeItem("price_active_ride_request_id");
          setRateLive(req);
          setLiveRequest(null);
          setView("search");
        } else if (req.status === "cancelled" || req.status === "expired") {
          localStorage.removeItem("price_active_ride_request_id");
          setLiveRequest(null);
          setView("search");
          const refundMsg = ["refunded", "refunding"].includes(req.payment_status) ? " — tu as été remboursé" : "";
          if (req.status === "expired") {
            toast.error(`Aucun covoitureur n'a accepté ta demande${refundMsg}`, { duration: 8000 });
          } else if (req.cancelled_by === "driver") {
            toast.error(`Le covoitureur a annulé : ${req.cancel_reason_label || "Course annulée"}${refundMsg}`);
          } else {
            toast(`Demande annulée${refundMsg}`);
          }
        }
      } catch (_e) { /* silencieux */ }
    }, 3000);
    return () => clearInterval(t);
  }, [liveRequest?.id, view]);

  const submitLiveRating = async ({ stars, comment }) => {
    if (!rateLive?.id) return;
    await api.post(`/price/ride-requests/${rateLive.id}/rate-driver`, { stars, comment });
  };

  const submitDriverRating = async ({ stars, comment }) => {
    const seatId = rateSeat?.id;
    if (!seatId) return;
    await api.post(`/price/seats/${seatId}/rate-driver`, { stars, comment });
    // Optimistic update
    setMySeats((cur) =>
      cur.map((s) => (s.id === seatId ? { ...s, rated_by_client: true } : s))
    );
  };

  const handleLogout = async () => {
    await logout();
    nav("/", { replace: true });
  };

  // Si le passager a téléversé une photo lors de l'inscription, on
  // l'envoie discrètement une fois qu'il est loggé.
  useEffect(() => { flushPendingPhoto(); }, []);

  // While searching, hijack the entire viewport with the waiting screen.
  // AdminPopupCenter : monté dans TOUTES les vues du passager (search,
  // waiting, results, live_waiting, driver_found) pour que les popups
  // admin (ex. "Dis-nous ta ville") arrivent toujours, même si
  // l'utilisateur est en recherche ou pairé avec un covoitureur.
  const popupCenter = (
    <>
    <WearNegotiationPopup />
    <DocumentsRequiredPopup role="client" />
    <AdminPopupCenter
      onActionTarget={(target) => {
        if (target === "history") setHistoryOpen(true);
        else setProfileSheetOpen(true);
      }}
    />
    </>
  );

  if (confirmRide) {
    return (
      <>
        <ScheduledConfirmScreen
          ride={scheduledRides.find((r) => r.id === confirmRide.id) || confirmRide}
          onOpenList={() => { setConfirmRide(null); setScheduledOpen(true); }}
          onHome={() => setConfirmRide(null)}
        />
        {popupCenter}
      </>
    );
  }

  if (view === "published") {
    return (
      <>
        <PublishedTripsScreen onBack={() => setView("search")} onBooked={loadMySeats} />
        {popupCenter}
      </>
    );
  }

  if (view === "live_waiting") {
    return (
      <>
        <LiveWaitingScreen
          request={liveRequest}
          onCancel={cancelLiveRequest}
        />
        {popupCenter}
      </>
    );
  }

  if (view === "driver_found") {
    return (
      <>
        <DriverFoundScreen
          request={liveRequest}
          onCancel={cancelLiveRequest}
        />
        {popupCenter}
      </>
    );
  }

  return (
    <div
      className="relative min-h-screen text-foreground street-dots"
      style={{ background: "hsl(15 100% 96%)" }}
      data-testid="client-dashboard"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(354, 88%, 66%, 0.35), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 bottom-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, hsla(38, 95%, 62%, 0.32), transparent 70%)" }}
      />

      <div
        className="relative mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(20px, env(safe-area-inset-top))",
          paddingBottom: "calc(110px + env(safe-area-inset-bottom))",
          paddingLeft: "max(20px, env(safe-area-inset-left))",
          paddingRight: "max(20px, env(safe-area-inset-right))",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="relative h-11 w-11 rounded-2xl bg-primary grid place-items-center rotate-[-6deg] street-border street-shadow-sm">
              <span className="font-[Outfit] text-lg font-black text-primary-foreground">P</span>
              <span className="absolute -right-1.5 -bottom-1.5 h-3 w-3 rounded-full bg-accent street-border-thin" />
            </div>
            <div>
              <div className="font-[Outfit] text-lg font-black leading-none">Price</div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-foreground/50">Covoiturage</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setProfileSheetOpen(true)}
              className="street-press inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-white street-border-thin street-shadow-sm"
              aria-label="Mon compte"
              data-testid="client-menu-open"
            >
              <Menu className="h-4 w-4 text-foreground" strokeWidth={2.75} />
            </button>
          </div>
        </div>

        {tab === "activity" && <div className="px-5 pt-3"><MyOffersPanel me="passenger" /></div>}
        {tab === "activity" && (
          <PriceHubPanel
            title="Activité"
            subtitle="Tes réservations, courses programmées et trajets passés."
            testid="client-activity-panel"
            items={[
              { testid: "client-reservations-open", label: "Mes réservations", sub: "Places réservées sur les trajets publiés", icon: <Ticket className="h-5 w-5" strokeWidth={2.5} />, badge: pendingRatingSeats.length, onClick: () => { loadMySeats(); setReservationsOpen(true); } },
              { testid: "client-activity-scheduled", label: "Mes courses programmées", sub: scheduledRides.length ? `Prochaine : ${fmtSched(scheduledRides[0].scheduled_at)}` : "Aucune course programmée", icon: <Calendar className="h-5 w-5" strokeWidth={2.5} />, tone: "bg-white", onClick: () => { loadScheduled(); setScheduledOpen(true); } },
              { testid: "client-history-open", label: "Historique", sub: "Toutes tes courses terminées", icon: <RouteIcon className="h-5 w-5" strokeWidth={2.5} />, tone: "bg-[#FFE9D6]", onClick: () => setHistoryOpen(true) },
            ]}
          />
        )}

        {tab === "messages" && (
          <PriceHubPanel
            title="Messages"
            subtitle="Tes échanges avec les covoitureurs et l'équipe Price."
            testid="client-messages-panel"
            items={[
              { testid: "client-messages-trips", label: "Messages trajets", sub: "Questions aux covoitureurs des trajets publiés", icon: <MessageCircle className="h-5 w-5" strokeWidth={2.5} />, badge: threads.unread, onClick: () => { threads.reload(); setThreadsOpen(true); } },
              { testid: "client-messages-support", label: "Support Price", sub: "Une question ? L'équipe te répond", icon: <LifeBuoy className="h-5 w-5" strokeWidth={2.5} />, tone: "bg-white", onClick: () => window.dispatchEvent(new Event("price:open-support")) },
            ]}
          />
        )}

        {tab === "home" && (<>
        {pinnedSeat ? (
          <PinnedTripBanner
            testid="client-pinned-trip"
            text={`${pinnedSeat.date === new Date().toLocaleDateString("sv-SE") ? "Trajet en cours" : "Prochain trajet"} · ${pinnedSeat.driver_name || "Ton covoitureur"} · ${fmtDay(pinnedSeat.date)} ${pinnedSeat.time}`}
            onClick={() => { loadMySeats(); setReservationsOpen(true); }}
          />
        ) : scheduledRides[0] ? (
          <PinnedTripBanner
            testid="client-pinned-scheduled"
            text={`${scheduledRides[0].status === "scheduled_awaiting_payment" ? "À payer" : "Course programmée"} · ${fmtSched(scheduledRides[0].scheduled_at)}`}
            onClick={() => { loadScheduled(); setScheduledOpen(true); }}
          />
        ) : null}

        {!mode && (
          <div className="mt-6">
            <p className="text-sm font-bold uppercase tracking-widest text-foreground/50">Salut {firstName} 👋</p>
            <h1 className="mt-1 font-[Outfit] text-[34px] font-black leading-[1.02] tracking-tight">Où vas-tu ?</h1>
            <button
              type="button"
              onClick={() => setMode("instant")}
              className="street-press mt-4 flex h-14 w-full items-center gap-3 rounded-2xl bg-white px-4 text-left street-border street-shadow-sm"
              data-testid="client-destination-search"
            >
              <Search className="h-5 w-5 flex-none text-primary" strokeWidth={2.75} />
              <span className="truncate text-[15px] font-bold text-foreground/50">Rechercher une destination…</span>
            </button>
          </div>
        )}

        {/* Bannière promo globale — n'apparaît que si l'admin l'a activée. */}
        {!mode && <div className="mt-4">
          <PromoBanner />
        </div>}

        {pendingRatingSeats.length > 0 && (
          <button
            onClick={() => setRateSeat(pendingRatingSeats[0])}
            className="street-press mt-4 flex w-full items-center gap-3 rounded-2xl bg-accent p-3 text-left street-border-thin street-shadow-sm"
            data-testid="client-pending-rating-cta"
          >
            <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-white street-border-thin">
              <Star className="h-5 w-5 text-accent-foreground" strokeWidth={2.5} fill="currentColor" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-[Outfit] text-[13px] font-black leading-tight">
                Note ton dernier trajet ⭐
              </div>
              <div className="text-[11px] font-medium text-accent-foreground/80 truncate">
                {pendingRatingSeats[0].from_address} → {pendingRatingSeats[0].to_address}
              </div>
            </div>
          </button>
        )}

        {/* Photo de profil déplacée dans le sheet « Mon compte » (bouton
            Menu en haut à droite) pour désencombrer l'accueil. */}

        {!mode && (
          <>
            <ClientHomeChoices onPick={(k) => (k === "published" ? setView("published") : setMode(k))} />
            <NearbyTripsCarousel city={profile?.city} onOpen={() => setView("published")} />
            {scheduledRides.length > 0 && (
              <button
                type="button"
                onClick={() => { loadScheduled(); setScheduledOpen(true); }}
                className="street-press mt-3 flex w-full items-center gap-3 rounded-2xl bg-white p-3 text-left street-border-thin street-shadow-sm"
                data-testid="client-scheduled-open"
              >
                <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-accent street-border-thin">
                  <Calendar className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-[Outfit] text-[13px] font-black leading-tight">
                    Mes courses programmées ({scheduledRides.length})
                  </div>
                  <div className="text-[11px] font-medium text-foreground/60 truncate capitalize">
                    Prochaine : {fmtSched(scheduledRides[0].scheduled_at)}
                  </div>
                </div>
                {scheduledRides.some((r) => r.status === "scheduled_awaiting_payment") && (
                  <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-primary-foreground street-border-thin" data-testid="client-scheduled-pay-badge">
                    À payer
                  </span>
                )}
                <ArrowRight className="h-4 w-4 flex-none" strokeWidth={3} />
              </button>
            )}
          </>
        )}

        {mode && (
          <div className="mt-5 flex items-center gap-3" data-testid="client-mode-header">
            <button
              type="button"
              onClick={() => setMode(null)}
              className="street-press grid h-10 w-10 flex-none place-items-center rounded-full bg-white street-border-thin street-shadow-sm"
              aria-label="Retour"
              data-testid="client-mode-back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-foreground/50">
                {mode === "instant" ? "Maintenant" : "Plus tard"}
              </div>
              <div className="font-[Outfit] text-[19px] font-black leading-tight">
                {mode === "instant" ? "Demande instantanée" : "Demande programmée"}
              </div>
            </div>
          </div>
        )}

        {mode && (
        <form
          noValidate
          onSubmit={onSearch}
          className="mt-4 rounded-3xl bg-white p-5 street-border street-shadow"
          data-testid="client-search-form"
        >
          <div className="space-y-3">
            <AddressField
              label="Départ"
              icon={<MapPin className="h-4 w-4 text-primary" />}
              value={form.from}
              onChange={upd("from")}
              placeholder="Ex. Place Masséna"
              proximity={proximity}
              autoGeoloc
              onGeolocDone={(coord) => setProximity(coord)}
              onPick={(p) => {
                if (p.lng && p.lat) {
                  setProximity(`${p.lng},${p.lat}`);
                  setFromCoord({ lng: p.lng, lat: p.lat });
                }
              }}
              testid="client-search-from"
            />
            <AddressField
              label="Arrivée"
              icon={<MapPin className="h-4 w-4 text-accent" />}
              value={form.to}
              onChange={upd("to")}
              placeholder="Ex. Palais des Festivals"
              proximity={proximity}
              onPick={(p) => {
                if (p.lng && p.lat) setToCoord({ lng: p.lng, lat: p.lat });
              }}
              testid="client-search-to"
            />

            <div className="pt-1 grid grid-cols-1 gap-3">
              {mode === "scheduled" && (
                <ScheduleField value={scheduledAt} onChange={setScheduledAt} />
              )}
              <SeatsPill
                value={form.seats}
                onChange={(v) => setForm((f) => ({ ...f, seats: v }))}
                testid="client-search-seats"
              />
            </div>

            {/* Case "2 roues" — visible uniquement pour les passagers seuls.
                Si cochée, la recherche inclut les trajets moto/scooter (une
                seule place derrière le covoitureur, casque à sa charge). */}
            {Number(form.seats) === 1 && (
              <>
                <label
                  className="mt-3 flex items-center gap-3 rounded-2xl bg-white p-3 street-border-thin street-shadow-sm cursor-pointer"
                  data-testid="client-two-wheels-label"
                >
                  <input
                    type="checkbox"
                    checked={!!form.accept_two_wheels}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      accept_two_wheels: e.target.checked,
                      // Reset helmet quand la case redevient décochée.
                      has_helmet: e.target.checked ? f.has_helmet : null,
                    }))}
                    data-testid="client-two-wheels-check"
                    className="h-5 w-5 flex-none accent-primary"
                  />
                  <div
                    className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-[#FFF3CC] street-border-thin"
                    data-testid="client-two-wheels-icon"
                  >
                    <PriceScooterIcon size={30} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-[Outfit] text-[13px] font-black leading-none">
                      J&apos;accepte les 2 roues (moto/scooter)
                    </div>
                    <div className="mt-1 text-[11px] font-medium text-foreground/60 leading-snug">
                      Tu seras passager derrière le covoitureur.
                    </div>
                  </div>
                </label>

                {/* Casque : radio Oui / Non — visible seulement si case 2 roues cochée */}
                {form.accept_two_wheels && (
                  <div
                    className="mt-2 rounded-2xl bg-white p-3 street-border-thin street-shadow-sm"
                    data-testid="client-helmet-block"
                  >
                    <div className="text-[11px] font-black uppercase tracking-widest text-foreground/60 mb-2">
                      Casque
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: true, label: "J'ai un casque", testid: "client-helmet-yes" },
                        { v: false, label: "Je n'en ai pas", testid: "client-helmet-no" },
                      ].map((o) => {
                        const active = form.has_helmet === o.v;
                        return (
                          <button
                            key={String(o.v)}
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, has_helmet: o.v }))}
                            data-testid={o.testid}
                            className={
                              "street-press rounded-xl py-2 text-[12px] font-black uppercase tracking-widest street-border-thin " +
                              (active ? "bg-primary text-primary-foreground" : "bg-[#FFF8F1] text-foreground")
                            }
                          >
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[10px] font-medium text-foreground/60 leading-snug">
                      Sans casque, le covoitureur pourra refuser ou t&apos;en fournir un — à voir avec lui.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Live price estimate — appears once both addresses are picked.
              Si une promo est active, on affiche le prix brut barré +
              le prix réduit en gros + le libellé promo. */}
          {priceInfo && (
            <div className="mt-4 rounded-2xl bg-primary p-4 street-border street-shadow-sm animate-fade-up" data-testid="price-estimate">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-primary-foreground/80">
                    {promoPct > 0 ? `Estimation · -${promoPct}%` : "Estimation"}
                  </div>
                  {promoPct > 0 ? (
                    <div className="mt-0.5 flex items-baseline gap-2">
                      <span className="font-[Outfit] text-[14px] font-bold text-primary-foreground/70 line-through tabular-nums">
                        {priceInfo.total.toFixed(2)}€
                      </span>
                      <span className="font-[Outfit] text-[28px] font-black leading-none text-primary-foreground tabular-nums" data-testid="price-estimate-promo">
                        {priceInfo.discounted.toFixed(2)}€
                      </span>
                    </div>
                  ) : (
                    <div className="mt-0.5 font-[Outfit] text-[28px] font-black leading-none text-primary-foreground">
                      ~{priceInfo.total.toFixed(2)}€
                    </div>
                  )}
                  <div className="mt-1 text-[11px] font-medium text-primary-foreground/85">
                    ~{priceInfo.km.toFixed(1)} km · {priceInfo.perSeat.toFixed(2)} € / passager
                  </div>
                  {priceInfo.tierLabel && (
                    <div className="mt-1.5 rounded-lg bg-white/15 px-2 py-1 text-[10px] font-bold leading-snug text-primary-foreground" data-testid="price-estimate-scheduled-tier">
                      {priceInfo.tierLabel} (au lieu de {priceInfo.instant.toFixed(2)}€ en immédiat)
                    </div>
                  )}
                  {priceInfo.long && (
                    <div className="mt-1.5 rounded-lg bg-white/15 px-2 py-1 text-[10px] font-bold leading-snug text-primary-foreground" data-testid="price-estimate-long">
                      Partage des frais : carburant {priceInfo.long.fuel_eur.toFixed(2)}€ + péages ~{priceInfo.long.toll_eur.toFixed(2)}€ ÷ {priceInfo.long.divisor} (le conducteur paie sa part)
                    </div>
                  )}
                </div>
                <div className="text-right">
                  {promoPct > 0 ? (
                    <>
                      <div className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 street-border-thin">
                        <span className="text-[12px]">🔥</span>
                        <span className="text-[11px] font-black uppercase tracking-widest text-primary">
                          Promo -{promoPct}%
                        </span>
                      </div>
                      <div className="mt-1.5 text-[11px] font-black text-primary-foreground tabular-nums">
                        Économie : {priceInfo.discount.toFixed(2)}€
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 street-border-thin">
                        <Zap className="h-3 w-3 text-accent-foreground" strokeWidth={2.5} />
                        <span className="text-[11px] font-black uppercase tracking-widest text-accent-foreground">
                          Trajet
                        </span>
                      </div>
                      <div className="mt-1.5 text-[11px] font-medium text-primary-foreground/85">
                        Tout compris
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {negotiable && (
            <PassengerPriceChooser recommended={Number(priceInfo.total.toFixed(2))} value={proposedPrice} onChange={setProposedPrice} />
          )}

          {/* Payment method — cash or card. Card triggers a Stripe Checkout
              redirect on submit (Apple Pay + saved cards supported). */}
          <div className="mt-4 grid grid-cols-2 gap-2">
            <PayMethodPill
              active={paymentMethod === "cash"}
              onClick={() => setPaymentMethod("cash")}
              icon={<Zap className="h-4 w-4" strokeWidth={2.5} />}
              label="Espèces"
              sub="Paye directement au covoitureur"
              testid="pay-method-cash"
            />
            <PayMethodPill
              active={paymentMethod === "card"}
              onClick={() => setPaymentMethod("card")}
              icon={<CreditCard className="h-4 w-4" strokeWidth={2.5} />}
              label="Carte"
              sub="Apple Pay + cartes sauvées"
              testid="pay-method-card"
            />
          </div>

          <button
            type="submit"
            disabled={!canSearch}
            className="street-press mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 font-[Outfit] text-[16px] font-black tracking-tight text-primary-foreground street-border street-shadow disabled:opacity-60"
            data-testid="client-search-submit"
          >
            {mode === "scheduled" ? <Calendar className="h-5 w-5" strokeWidth={2.5} /> : <Zap className="h-5 w-5" strokeWidth={2.5} />}
            {mode === "scheduled" ? "Programmer ma course" : "Envoyer la demande"}
          </button>
          {mode === "scheduled" && paymentMethod === "card" && (
            <p className="mt-2 text-center text-[11px] font-semibold text-foreground/60" data-testid="client-scheduled-card-note">
              Tu paieras seulement quand un covoitureur aura accepté ta course.
            </p>
          )}
        </form>
        )}
        </>)}

        <div className="mt-8 text-center text-[11px] font-bold uppercase tracking-widest text-foreground/40">
          Price · Covoiturage
        </div>
      </div>

      <PriceBottomTabs
        prefix="client"
        active={profileSheetOpen ? "settings" : tab}
        badges={{ activity: pendingRatingSeats.length, messages: threads.unread }}
        onChange={(k) => {
          if (k === "settings") { setProfileSheetOpen(true); return; }
          setProfileSheetOpen(false);
          if (k === "activity") { loadMySeats(); loadScheduled(); }
          if (k === "home") setMode(null);
          setTab(k);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
      <TripThreadsSheet open={threadsOpen} onClose={() => { setThreadsOpen(false); threads.reload(); }} role="client" threads={threads} />

      <ReservationsSheet
        open={reservationsOpen}
        onClose={() => setReservationsOpen(false)}
        seats={mySeats}
        onCancelSeat={cancelSeat}
        onRate={(seat) => setRateSeat(seat)}
      />

      <RateModal
        open={!!rateSeat}
        onClose={() => setRateSeat(null)}
        onSubmit={submitDriverRating}
        title="Noter ton covoitureur"
        subtitle={
          rateSeat
            ? `${rateSeat.driver_name} · ${rateSeat.from_address} → ${rateSeat.to_address}`
            : ""
        }
        target_label="covoitureur"
      />

      <RateModal
        open={!!rateLive}
        onClose={() => setRateLive(null)}
        onSubmit={submitLiveRating}
        title="Noter ton covoitureur"
        subtitle={
          rateLive
            ? `${rateLive.driver_name} · ${rateLive.from_address} → ${rateLive.to_address}`
            : ""
        }
        target_label="covoitureur"
      />

      <HistorySheet open={historyOpen} onClose={() => setHistoryOpen(false)} />

      <ScheduledRidesSheet
        open={scheduledOpen}
        onClose={() => setScheduledOpen(false)}
        rides={scheduledRides}
        onReload={loadScheduled}
        onFollow={followRide}
      />

      <PriceProfileSheet
        open={profileSheetOpen}
        onClose={() => setProfileSheetOpen(false)}
        variant="client"
        onLogout={async () => { setProfileSheetOpen(false); await handleLogout(); }}
        onHistory={() => setHistoryOpen(true)}
        displayName={profile?.full_name || (profile ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() : "") || "Passager"}
        displayEmail={user?.email || profile?.email}
      />

      {/* Popup "Question de Price" — s'affiche automatiquement si l'admin
          a envoyé un sondage non-répondu. Poll /price/me/surveys/pending. */}
      <PriceSurveyPopup />

      <WearNegotiationPopup />
      <DocumentsRequiredPopup role="client" />

      <AdminPopupCenter
        onActionTarget={(target) => {
          // Client : les cibles Stripe/wallet ne s'appliquent pas.
          // Toutes les actions "profile_photo" / "profile" ouvrent le
          // sheet compte. History ouvre l'historique.
          if (target === "history") setHistoryOpen(true);
          else setProfileSheetOpen(true);
        }}
      />

      <PriceSupportFAB />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Chunky pill controls for Date + Passagers, spaced from the address */
/* fields above so nothing "touches" the outer card border.           */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/* Payment method pill — cash vs card                                   */
/* ------------------------------------------------------------------ */
function PayMethodPill({ active, onClick, icon, label, sub, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`street-press flex items-start gap-2 rounded-2xl p-3 text-left street-border-thin street-shadow-sm transition-colors ${
        active
          ? "bg-primary text-primary-foreground"
          : "bg-white text-foreground"
      }`}
      data-testid={testid}
      aria-pressed={active}
    >
      <div
        className={`grid h-9 w-9 flex-none place-items-center rounded-xl street-border-thin ${
          active ? "bg-white text-primary" : "bg-primary/10 text-primary"
        }`}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-[Outfit] text-[13px] font-black leading-tight">{label}</div>
        <div className={`mt-0.5 text-[10px] font-medium leading-tight ${
          active ? "text-primary-foreground/85" : "text-foreground/60"
        }`}>
          {sub}
        </div>
      </div>
    </button>
  );
}


function SeatsPill({ value, onChange, testid }) {
  const n = Math.max(1, Math.min(4, Number(value) || 1));
  const dec = () => onChange(Math.max(1, n - 1));
  const inc = () => onChange(Math.min(4, n + 1));
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin street-shadow-sm">
      <div className="flex items-center gap-2">
        <div className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-accent/25 street-border-thin">
          <Users className="h-4 w-4 text-foreground" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50 leading-none">
            Passagers
          </div>
          <div className="mt-1 flex items-center justify-between gap-1">
            <button
              type="button"
              onClick={dec}
              disabled={n <= 1}
              aria-label="Moins un passager"
              className="street-press grid h-6 w-6 place-items-center rounded-md bg-secondary text-foreground street-border-thin text-[13px] font-black disabled:opacity-40"
              data-testid={`${testid}-dec`}
            >−</button>
            <span
              className="font-[Outfit] text-[16px] font-black leading-none tabular-nums"
              data-testid={testid}
            >{n}</span>
            <button
              type="button"
              onClick={inc}
              disabled={n >= 4}
              aria-label="Plus un passager"
              className="street-press grid h-6 w-6 place-items-center rounded-md bg-primary text-primary-foreground street-border-thin text-[13px] font-black disabled:opacity-40"
              data-testid={`${testid}-inc`}
            >+</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* RoadScene — décor cartoon réutilisé (Waiting + LiveWaiting)         */
/* ------------------------------------------------------------------ */
function RoadScene() {
  return (
    <div className="mt-6 mx-4 rounded-3xl street-border street-shadow overflow-hidden flex-1 relative min-h-[260px]">
      <div
        className="absolute inset-x-0 top-0 h-2/3"
        style={{
          background: "linear-gradient(180deg, hsl(200 90% 82%) 0%, hsl(200 90% 92%) 100%)",
        }}
      />
      <div
        className="absolute h-14 w-14 rounded-full bg-accent street-border-thin"
        style={{ top: 20, right: 24, boxShadow: "0 0 0 6px hsl(38 95% 82%)" }}
      />

      {/* Wordmark "Price" flottant dans le ciel — rappel de marque
          pendant que le passager attend le covoitureur. Reste au-dessus
          des nuages qui bougent, ne masque pas la voiture qui roule. */}
      <div
        className="absolute left-1/2 top-8 -translate-x-1/2 pointer-events-none select-none z-[2] price-sky-bob"
        data-testid="road-sky-price"
      >
        <span
          className="block font-[Outfit] text-[46px] leading-none font-black tracking-[-0.04em] text-primary"
          style={{
            WebkitTextStroke: "1.5px hsl(355 55% 12%)",
            textShadow: "0 3px 0 hsl(355 55% 12%), 0 8px 18px rgba(15,38,52,0.18)",
          }}
        >
          Price
        </span>
      </div>

      <div className="absolute top-10 left-6 h-4 w-16 rounded-full bg-white/95 street-border-thin animate-bounce-soft" />
      <div className="absolute top-20 right-24 h-3.5 w-12 rounded-full bg-white/95 street-border-thin animate-bounce-soft" style={{ animationDelay: "0.4s" }} />
      <div className="absolute top-28 left-1/3 h-3.5 w-14 rounded-full bg-white/95 street-border-thin animate-bounce-soft" style={{ animationDelay: "1s" }} />

      <div className="absolute inset-x-0" style={{ bottom: "33%" }}>
        <svg viewBox="0 0 400 80" className="w-full h-20" preserveAspectRatio="none">
          <path d="M0,80 Q60,20 120,50 T240,40 T400,60 L400,80 Z" fill="hsl(120 40% 68%)" stroke="hsl(355 55% 12%)" strokeWidth="3" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="absolute inset-x-0 bottom-0 h-1/3" style={{ background: "hsl(120 45% 72%)" }} />

      <div
        className="absolute inset-x-0 h-20 bg-neutral-800"
        style={{
          bottom: "12%",
          borderTop: "3px solid hsl(355 55% 12%)",
          borderBottom: "3px solid hsl(355 55% 12%)",
        }}
      >
        <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 road-scroll" />
      </div>

      <div
        className="absolute inset-x-0 h-20 flex items-center animate-car-drive"
        style={{ bottom: "13%" }}
      >
        <div className="relative flex items-center">
          <div className="absolute right-full top-2 mr-2 h-4 w-4 rounded-full bg-white/90 street-border-thin animate-exhaust-puff" />
          <div className="absolute right-full top-5 mr-5 h-3 w-3 rounded-full bg-white/80 street-border-thin animate-exhaust-puff" style={{ animationDelay: "0.25s" }} />
          <div className="absolute right-full top-3 mr-9 h-2.5 w-2.5 rounded-full bg-white/70 street-border-thin animate-exhaust-puff" style={{ animationDelay: "0.5s" }} />
          <BigCartoonCar />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Live waiting — demande live envoyée, poll de l'acceptation          */
/* ------------------------------------------------------------------ */
function LiveWaitingScreen({ request, onCancel }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  return (
    <div
      className="fixed inset-0 z-[100] flex justify-center overflow-y-auto overscroll-contain text-foreground"
      style={{ background: "hsl(15 100% 96%)" }}
      data-testid="client-live-waiting"
    >
      <div className="relative flex w-full max-w-md flex-col street-dots">
        <div
          className="flex items-center justify-between px-5"
          style={{ paddingTop: "max(20px, env(safe-area-inset-top))" }}
        >
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-primary-foreground street-border-thin street-shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-ping" /> Demande live
          </div>
          <button
            onClick={onCancel}
            className="street-press inline-flex h-10 items-center gap-1.5 rounded-full bg-white px-3 text-[12px] font-black uppercase tracking-widest text-foreground street-border-thin street-shadow-sm"
            data-testid="client-cancel-live"
          >
            <X className="h-4 w-4" />
            Annuler
          </button>
        </div>

        <div className="mt-6 px-5 text-center">
          <h1 className="font-[Outfit] text-[28px] font-black leading-[1.05] tracking-tight">
            En attente<br />
            <span className="inline-block rounded-xl bg-accent px-2 py-0.5 mt-1 street-border-thin rotate-[-2deg] text-accent-foreground">
              d&apos;un covoitureur
            </span>
          </h1>
          <p className="mt-3 text-sm font-medium text-foreground/70">
            Ta demande est diffusée aux covoitureurs Price connectés autour de toi 📡
          </p>
        </div>

        <RoadScene />

        <div
          className="px-5 pt-4"
          style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
        >
          {request?.negotiable && (
            <div className="mb-3 rounded-3xl bg-[#FFF8F1] p-3 street-border">
              <RequestOffersPanel requestId={request.id} recommended={request.recommended_price_eur} />
            </div>
          )}
          <div className="rounded-3xl bg-white p-4 street-border street-shadow">
            <div className="flex items-start gap-3">
              <RoutePoints
                from={request?.from_address || "Départ"}
                to={request?.to_address || "Arrivée"}
                size="sm"
                truncate
                className="flex-1"
              />
              <div className="text-right">
                <div className="text-[10px] font-bold uppercase tracking-widest text-foreground/50">Temps</div>
                <div className="font-mono font-black text-[16px] text-primary">{mm}:{ss}</div>
                <div className="mt-1 text-[10px] font-bold text-foreground/50">
                  {Number(request?.gross_price_eur || 0) > Number(request?.price_eur || 0) ? (
                    <span className="inline-flex items-baseline gap-1">
                      <span className="line-through opacity-60 tabular-nums">
                        {Number(request.gross_price_eur).toFixed(2)}€
                      </span>
                      <span className="text-primary tabular-nums">
                        {Number(request.price_eur || 0).toFixed(2)}€
                      </span>
                    </span>
                  ) : (
                    <span className="font-[Outfit] text-[15px] font-black text-foreground tabular-nums">{request?.negotiable && !request?.negotiated ? "Reco. " : ""}{Number(request?.price_eur || 0).toFixed(2)}€</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Driver found — un covoitureur a accepté, affichage de ses infos       */
/* ------------------------------------------------------------------ */
function DriverFoundScreen({ request, onCancel }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [boarding, setBoarding] = useState(false);
  const [unread, setUnread] = useState(0);
  const status = request?.status || "accepted";

  // Badge "messages non lus" sur le bouton Message — polling discret
  // toutes les 10 s tant que le chat est fermé. Pausé onglet caché.
  useEffect(() => {
    if (!request?.id || chatOpen) { setUnread(0); return; }
    let cancel = false;
    const fetchUnread = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const r = await api.get(`/price/ride-requests/${request.id}/messages/unread-count`);
        if (cancel) return;
        const n = Number(r.data?.count || 0);
        setUnread((prev) => {
          if (n > prev && n > 0) {
            try {
              if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(45);
            } catch (_e) { /* ignore */ }
          }
          return n;
        });
      } catch (_e) { /* silencieux */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 10000);
    return () => { cancel = true; clearInterval(t); };
  }, [request?.id, chatOpen]);

  // Chrono depuis l'acceptation — utile pour donner un ordre de grandeur
  // au passager. On tick chaque seconde.
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    const anchor = request?.accepted_at ? new Date(request.accepted_at).getTime() : Date.now();
    const tick = () => setElapsedSec(Math.max(0, Math.floor((Date.now() - anchor) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [request?.accepted_at]);

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const confirmBoarding = async () => {
    if (!request?.id || boarding) return;
    setBoarding(true);
    try {
      await api.post(`/price/ride-requests/${request.id}/passenger-onboard`);
      toast.success("Confirmé — le covoitureur va démarrer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBoarding(false);
    }
  };

  // Badge visuel selon le statut courant.
  const statusBadge = {
    accepted: { label: "Covoitureur en route", color: "bg-accent", dot: "bg-primary", pulse: true },
    passenger_boarding: { label: "En attente du covoitureur", color: "bg-amber-300", dot: "bg-amber-700", pulse: true },
    in_progress: { label: "Course en cours", color: "bg-emerald-400", dot: "bg-emerald-900", pulse: true },
  }[status] || { label: "Course active", color: "bg-accent", dot: "bg-primary", pulse: true };

  const driverInitial = (request?.driver_name || "?").slice(0, 1).toUpperCase();
  const rating = Number(request?.driver_rating || 0);
  const ridesCount = Number(request?.driver_rides_count || 0);
  const priceEur = Number(request?.price_eur || 0);
  const walletApplied = Number(request?.wallet_applied_eur || 0);
  const toPay = Math.max(0, priceEur - walletApplied);

  return (
    <div
      className="relative min-h-screen text-foreground street-dots"
      style={{ background: "hsl(15 100% 96%)" }}
      data-testid="client-driver-found"
    >
      <div
        className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col"
        style={{
          paddingTop: "max(20px, env(safe-area-inset-top))",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
          paddingLeft: "max(20px, env(safe-area-inset-left))",
          paddingRight: "max(20px, env(safe-area-inset-right))",
        }}
      >
        {/* Header status badge + chrono */}
        <div className="flex items-center justify-between gap-2">
          <div className={`inline-flex items-center gap-1.5 rounded-full ${statusBadge.color} px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-foreground street-border-thin street-shadow-sm`}>
            <span className={`h-1.5 w-1.5 rounded-full ${statusBadge.dot} ${statusBadge.pulse ? "animate-ping" : ""}`} />
            {statusBadge.label}
          </div>
          <div className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-black tabular-nums street-border-thin street-shadow-sm">
            <Clock className="h-3 w-3" strokeWidth={2.75} />
            {mmss(elapsedSec)}
          </div>
        </div>

        <h1 className="mt-4 font-[Outfit] text-[26px] font-black leading-tight" data-testid="driver-found-title">
          {status === "in_progress"
            ? "En route vers ta destination 🚗"
            : status === "passenger_boarding"
              ? "Attends que le covoitureur confirme…"
              : `${request?.driver_name || "Ton covoitureur"} arrive 🎉`}
        </h1>

        {/* Carte covoitureur — photo + note + stats */}
        <div className="mt-5 rounded-3xl bg-white p-5 street-border street-shadow">
          <div className="flex items-center gap-4">
            {request?.driver_photo_url ? (
              <img
                src={request.driver_photo_url}
                alt={request.driver_name || "Covoitureur"}
                className="h-20 w-20 flex-none rounded-2xl object-cover street-border-thin street-shadow-sm"
                data-testid="driver-found-photo"
              />
            ) : (
              <div className="grid h-20 w-20 flex-none place-items-center rounded-2xl bg-primary text-primary-foreground street-border-thin street-shadow-sm">
                <span className="font-[Outfit] text-[30px] font-black">{driverInitial}</span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-[Outfit] text-[20px] font-black leading-tight truncate" data-testid="driver-found-name">
                {request?.driver_name || "Covoitureur"}
              </div>
              <div className="mt-1 flex items-center gap-1 text-[13px] font-bold text-foreground/70" data-testid="driver-found-rating">
                <Star className="h-4 w-4 text-primary fill-primary" strokeWidth={2.5} />
                {rating > 0 ? rating.toFixed(1).replace(".", ",") : "Nouveau"}
                {ridesCount > 0 && (
                  <span className="text-foreground/40">· {ridesCount} course{ridesCount > 1 ? "s" : ""}</span>
                )}
              </div>
              {request?.driver_car && (
                <div className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-bold text-foreground/60">
                  <Car className="h-3.5 w-3.5" /> {request.driver_car}
                </div>
              )}
              {request?.driver_plate && (
                <div className="mt-1 inline-flex items-center rounded-md bg-accent/30 px-2 py-0.5 text-[11px] font-mono font-bold street-border-thin">
                  {request.driver_plate}
                </div>
              )}
            </div>
          </div>

          {/* Actions contact */}
          <div className={"mt-4 grid gap-2 " + (request?.driver_phone ? "grid-cols-2" : "grid-cols-1")}>
            {request?.driver_phone && (
              <a
                href={`tel:${request.driver_phone}`}
                className="street-press inline-flex items-center justify-center gap-1.5 rounded-2xl bg-primary py-3 font-[Outfit] text-[13px] font-black text-primary-foreground street-border-thin street-shadow-sm"
                data-testid="driver-found-call"
              >
                <Phone className="h-4 w-4" strokeWidth={2.5} /> Appeler
              </a>
            )}
            <button
              type="button"
              onClick={() => setChatOpen(true)}
              className="street-press relative inline-flex items-center justify-center gap-1.5 rounded-2xl bg-white py-3 font-[Outfit] text-[13px] font-black street-border-thin street-shadow-sm"
              data-testid="driver-found-chat"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.5} /> Message
              {unread > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[11px] font-black street-border-thin animate-pulse"
                  data-testid="client-chat-unread-badge"
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Timeline itinéraire */}
        <div className="mt-4 rounded-3xl bg-white p-5 street-border street-shadow">
          <div className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
            Itinéraire
          </div>
          <div className="mt-3">
            <RoutePoints
              from={request?.from_address}
              to={request?.to_address}
              size="sm"
              className="flex-1"
            />
          </div>
        </div>

        {/* Prix détaillé — avec prix barré si une promo a été appliquée. */}
        <div className="mt-4 rounded-3xl bg-white p-5 street-border street-shadow" data-testid="driver-found-price-box">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-black uppercase tracking-widest text-foreground/50">
              Prix du trajet
            </div>
            <div className="font-[Outfit] text-[18px] font-black tabular-nums">
              {priceEur.toFixed(2).replace(".", ",")} €
            </div>
          </div>
          {Number(request?.promo_percent_off || 0) > 0 && Number(request?.gross_price_eur || 0) > priceEur && (
            <div className="mt-2 flex items-center justify-between text-[12px] font-bold" data-testid="driver-found-promo-row">
              <span className="inline-flex items-center gap-1 text-primary">
                🔥 Promo -{request.promo_percent_off}%
              </span>
              <span className="tabular-nums text-foreground/60 line-through">
                {Number(request.gross_price_eur).toFixed(2).replace(".", ",")} €
              </span>
            </div>
          )}
          {walletApplied > 0 && (
            <div className="mt-2 flex items-center justify-between text-[12px] font-bold">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <Ticket className="h-3.5 w-3.5" /> Portefeuille utilisé
              </span>
              <span className="tabular-nums text-emerald-700">− {walletApplied.toFixed(2).replace(".", ",")} €</span>
            </div>
          )}
          <div className="mt-2 flex items-center justify-between border-t-2 border-black/10 pt-2">
            <span className="text-[12px] font-black uppercase tracking-widest text-foreground/70">
              {request?.payment_method === "card" ? "Payé par carte" : "À payer au covoitureur"}
            </span>
            <span className="font-[Outfit] text-[20px] font-black text-primary tabular-nums">
              {toPay.toFixed(2).replace(".", ",")} €
            </span>
          </div>
        </div>

        {/* Bouton de confirmation d'embarquement — visible uniquement
            quand la course est acceptée et que le passager n'a pas
            encore confirmé. */}
        {status === "accepted" && (
          <button
            type="button"
            onClick={confirmBoarding}
            disabled={boarding}
            className="street-press-lg mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-4 font-[Outfit] text-[15px] font-black uppercase tracking-wider text-white street-border street-shadow disabled:opacity-60"
            data-testid="client-onboard-confirm"
          >
            <Check className="h-5 w-5" strokeWidth={3} />
            {boarding ? "Envoi…" : "Je suis dans le véhicule"}
          </button>
        )}

        {/* En attente confirmation covoitureur */}
        {status === "passenger_boarding" && (
          <div
            className="mt-5 rounded-2xl bg-amber-100 p-4 text-center street-border street-shadow"
            data-testid="client-waiting-driver-confirm"
          >
            <div className="font-[Outfit] text-[14px] font-black text-amber-900">
              ⏳ En attente du covoitureur
            </div>
            <p className="mt-1 text-[12px] font-medium text-amber-800/80 leading-snug">
              {request?.driver_name || "Le covoitureur"} confirme ta présence à bord pour démarrer.
            </p>
          </div>
        )}

        {/* Course lancée */}
        {status === "in_progress" && (
          <div
            className="mt-5 rounded-2xl bg-emerald-100 p-4 text-center street-border street-shadow"
            data-testid="client-ride-in-progress"
          >
            <div className="font-[Outfit] text-[14px] font-black text-emerald-900">
              🚗 En route ! Bon voyage
            </div>
            <p className="mt-1 text-[12px] font-medium text-emerald-800/80 leading-snug">
              Le covoitureur t'amène à destination. Tu recevras une notification à l'arrivée.
            </p>
          </div>
        )}

        {/* Annulation — masquée en cours de trajet pour éviter les
            annulations accidentelles à mi-course. */}
        {status !== "in_progress" && (
          <button
            onClick={onCancel}
            className="street-press mt-4 w-full rounded-2xl bg-white py-3 font-[Outfit] text-[13px] font-black text-foreground/70 street-border-thin street-shadow-sm"
            data-testid="driver-found-cancel"
          >
            Annuler la course
          </button>
        )}

        <div className="mt-auto pt-8 text-center text-[11px] font-bold uppercase tracking-widest text-foreground/40">
          Price · Covoiturage
        </div>
      </div>
      <ChatSheet
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        requestId={request?.id}
        selfRole="client"
        counterpartName={request?.driver_name || "Covoitureur"}
      />
    </div>
  );
}

function Chip({ icon, children }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary/60 px-2.5 py-1 text-[11px] font-black street-border-thin">
      {icon}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Feuille "Mes réservations" — liste des réservations passager        */
/* ------------------------------------------------------------------ */
function ReservationsSheet({ open, onClose, seats, onCancelSeat, onRate }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[110] flex justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="reservations-sheet"
    >
      <div
        className="relative w-full max-w-md overflow-y-auto bg-white street-border street-shadow"
        style={{
          borderRadius: "24px 24px 0 0",
          marginTop: "auto",
          maxHeight: "92vh",
          paddingBottom: "max(24px, env(safe-area-inset-bottom))",
        }}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-5 py-4 street-border-thin"
             style={{ borderTop: 0, borderLeft: 0, borderRight: 0 }}>
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground street-border-thin">
              <Ticket className="h-4 w-4" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-[Outfit] text-[16px] font-black leading-none">Mes trajets</div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-foreground/50">
                {seats.length} réservation{seats.length > 1 ? "s" : ""}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="street-press grid h-9 w-9 place-items-center rounded-full bg-white street-border-thin street-shadow-sm"
            aria-label="Fermer"
            data-testid="reservations-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-3">
          {seats.length === 0 && (
            <div className="rounded-3xl bg-secondary/30 p-6 text-center text-[13px] font-medium text-foreground/60 street-border-thin">
              Aucun trajet pour le moment. Fais ta première recherche !
            </div>
          )}

          {seats.map((s) => (
            <SeatCard
              key={s.id}
              seat={s}
              onCancel={() => onCancelSeat(s.id)}
              onRate={() => onRate(s)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function SeatCard({ seat, onCancel, onRate }) {
  const dateLabel = (() => {
    try {
      return new Date(seat.date + "T00:00:00").toLocaleDateString("fr-FR", {
        weekday: "short", day: "numeric", month: "short",
      });
    } catch (_e) { return seat.date; }
  })();
  const map = {
    confirmed: { label: "Confirmé", bg: "bg-accent", fg: "text-accent-foreground" },
    completed: { label: "Terminé", bg: "bg-primary", fg: "text-primary-foreground" },
    cancelled: { label: "Annulé", bg: "bg-muted", fg: "text-foreground/60" },
  };
  const stat = map[seat.status] || map.confirmed;
  return (
    <div
      className={`rounded-3xl bg-white p-4 street-border street-shadow ${
        seat.status === "cancelled" ? "opacity-60" : ""
      }`}
      data-testid={`seat-card-${seat.id}`}
    >
      <div className="flex items-start gap-3">
        <RoutePoints
          from={seat.from_address}
          to={seat.to_address}
          size="sm"
          truncate
          className="flex-1"
        />
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-widest street-border-thin ${stat.bg} ${stat.fg}`}
        >
          {stat.label}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Chip icon={<Clock className="h-3.5 w-3.5" />}>{dateLabel} · {seat.time}</Chip>
        <Chip icon={<Users className="h-3.5 w-3.5" />}>
          {seat.seats} place{seat.seats > 1 ? "s" : ""}
        </Chip>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-black text-primary street-border-thin">
          {Number(seat.total_price_eur).toFixed(2)}€
        </span>
      </div>

      <div className="mt-2 text-[11px] font-medium text-foreground/60">
        Covoitureur : {seat.driver_name}
      </div>

      {seat.status === "confirmed" && (
        <button
          onClick={onCancel}
          className="street-press mt-3 w-full rounded-2xl bg-white py-2.5 text-[12px] font-black text-foreground street-border-thin street-shadow-sm"
          data-testid={`seat-cancel-${seat.id}`}
        >
          Annuler la réservation
        </button>
      )}
      {seat.status === "completed" && !seat.rated_by_client && (
        <button
          onClick={onRate}
          className="street-press mt-3 inline-flex w-full items-center justify-center gap-1 rounded-2xl bg-accent py-2.5 text-[13px] font-black text-accent-foreground street-border-thin street-shadow-sm"
          data-testid={`seat-rate-${seat.id}`}
        >
          <Star className="h-4 w-4" strokeWidth={2.5} /> Noter le covoitureur
        </button>
      )}
      {seat.status === "completed" && seat.rated_by_client && (
        <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-black text-foreground/60">
          <Check className="h-4 w-4" /> Covoitureur noté
        </div>
      )}
    </div>
  );
}

function BigCartoonCar() {
  return (
    <div className="animate-car-bounce">
      <svg
        width="140"
        height="80"
        viewBox="0 0 140 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8 52 L14 34 C18 24 28 20 38 20 L66 20 L78 6 L102 6 C110 6 116 12 120 22 L128 34 L132 52 Z"
          fill="hsl(354 88% 66%)"
          stroke="hsl(355 55% 12%)"
          strokeWidth="4"
          strokeLinejoin="round"
        />
        <path
          d="M66 20 L78 6 L102 6"
          stroke="hsl(355 55% 12%)"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <path
          d="M66 20 L78 9 L88 9 L88 20 Z"
          fill="hsl(200 90% 88%)"
          stroke="hsl(355 55% 12%)"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        <path
          d="M92 9 L102 9 C108 9 112 12 116 20 L92 20 Z"
          fill="hsl(200 90% 88%)"
          stroke="hsl(355 55% 12%)"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        <rect x="52" y="30" width="12" height="3" rx="1.5" fill="hsl(355 55% 12%)" />
        <circle cx="128" cy="40" r="3.5" fill="hsl(38 95% 62%)" stroke="hsl(355 55% 12%)" strokeWidth="2" />
        <rect x="8" y="36" width="6" height="6" rx="1.5" fill="hsl(0 78% 55%)" stroke="hsl(355 55% 12%)" strokeWidth="2" />
        <rect x="10" y="50" width="120" height="5" fill="hsl(355 55% 12%)" />
        <circle cx="80" cy="16" r="4" fill="hsl(38 95% 82%)" stroke="hsl(355 55% 12%)" strokeWidth="2" />
        <circle cx="94" cy="16" r="4" fill="hsl(38 95% 82%)" stroke="hsl(355 55% 12%)" strokeWidth="2" />
        <g style={{ transformOrigin: "30px 62px" }} className="animate-wheel-spin">
          <circle cx="30" cy="62" r="12" fill="hsl(355 55% 12%)" />
          <circle cx="30" cy="62" r="5" fill="hsl(0 0% 100%)" />
          <rect x="28.5" y="52" width="3" height="20" fill="hsl(0 0% 100%)" />
          <rect x="20" y="60.5" width="20" height="3" fill="hsl(0 0% 100%)" />
        </g>
        <g style={{ transformOrigin: "110px 62px" }} className="animate-wheel-spin">
          <circle cx="110" cy="62" r="12" fill="hsl(355 55% 12%)" />
          <circle cx="110" cy="62" r="5" fill="hsl(0 0% 100%)" />
          <rect x="108.5" y="52" width="3" height="20" fill="hsl(0 0% 100%)" />
          <rect x="100" y="60.5" width="20" height="3" fill="hsl(0 0% 100%)" />
        </g>
      </svg>
    </div>
  );
}
